/**
 * TasksPage — task management R1 (/tasks, behind `?x=tasks`). Two lists over the Scrips.Patient task store, plus the
 * care-model review steps that wait on a person:
 *   · My tasks   — live tasks I own (any role), and care-model reviews whose step names me;
 *   · Team queue — every live task in the practice (waiting ones first by the store's order: urgency, then due date),
 *                  and the care-model reviews no one is named for, or someone else is.
 * "New task" (physician, nurse, administrator) opens CreateTaskDialog.
 *
 * Live: the store lists are read every 30 s, the care-model reviews every 60 s, and both at once after a write here.
 * A failed read is one line in words, never an empty list.
 *
 * Look: the Patients list's (SHOWCASE-CHANGELOG "Patients", "Table", "Today"): the one PageHeader (title, one meta line,
 * New task the one primary action), the lists as HeaderTabs with their counts as Today's buckets, content at 48px sides,
 * 32px top and 48px bottom, one hairline card holding one DS Table. Loading, failed and empty states are the Patients
 * and Today ones (BrandLoading, EmptyState). A partial read is a DS Banner above the card.
 *
 * Pieces: the app's PageHeader + HeaderTabs (zone Z4); Signal DS PageScaffold, Table, Button, EmptyState, BrandLoading, Banner.
 */
import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import {
  Banner,
  BrandLoading,
  Button,
  EmptyState,
  PageScaffold,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@scripsteam/scrips-signal-ds';
import { useAuthStore } from '@/auth/auth-store';
import { isAlliedHealth, isNurse, isDoctor } from '@/auth/roles';
import { PageHeader } from '@/shared/ui/components/page-header';
import { V2_CARD_EDGE } from '@/shared/ui/design-v2';
import { HeaderTabs } from '@/shared/ui/components/header-tabs';
import type { TaskRole } from '@/tasks/lib/clinical-task-wire';
import { isTasksOn, mayCreateTask } from '@/tasks/lib/tasks-switch';
import { useTaskQueue, type TaskList } from '@/tasks/services/use-clinical-tasks';
import { isMyReview, useCareModelQueue, type CareModelReview } from '@/tasks/services/use-care-model-queue';
import { CareModelRow, STEP_UNREAD, TaskRow } from '@/tasks/components/task-rows';
import { tickBlock } from '@/patients/lib/care-model-tasks';
import { isCareModelTask, type ClinicalTask } from '@/tasks/lib/clinical-task-wire';
import { CreateTaskDialog } from '@/tasks/components/create-task-dialog';

export const STORE_ERROR = "Couldn't load the tasks.";
export const STORE_STALE = 'Could not refresh the tasks. This list may be out of date.';
export const CARE_MODEL_ERROR = 'Could not load the care-model reviews. This is not an empty list.';
export const TRUNCATED = 'This list is longer than the page reads (1,000 tasks). The most urgent come first.';
export const NO_TASKS_YET = 'No tasks so far';
export const STILL_READING_REVIEWS = 'Still reading care-model reviews…';
export const CARE_MODEL_PARTIAL ='Some care models could not be read, so this list may be missing a review.';

/** The table's column headers, in TaskRow's cell order (sentence case; the actions column's is for screen readers). */
const TASK_COLUMNS = ['Task', 'Patient', 'Source', 'Status', 'Due', 'Held by', ''] as const;

/** A tab label with its count in the header tab's count style (Today's buckets). */
function tabLabel(title: string, count: number | null): ReactNode {
  return count === null ? (
    title
  ) : (
    <>
      {title} <span className="header-tab-count">{count}</span>
    </>
  );
}

export function TasksPage() {
  if (!isTasksOn()) {
    return (
      <div className="p-6">
        <EmptyState icon="clipboard" title="Tasks are off in this browser" description="Open the app with ?x=tasks to turn them on." />
      </div>
    );
  }
  return <TasksView />;
}

function TasksView() {
  const user = useAuthStore((s) => s.user);
  const [tab, setTab] = useState<TaskList>('mine');
  const [creating, setCreating] = useState(false);
  const mine = useTaskQueue('mine');
  const team = useTaskQueue('team');
  const careModel = useCareModelQueue();
  const me = [user?.id, user?.userId, user?.practitionerId];
  const reviewHolder = (r: CareModelReview) =>
    isMyReview(r, me) ? 'Named to you' : (r.checklist.assigneeIds?.length ?? 0) > 0 ? 'Named to a colleague' : 'Anyone in the practice';
  const myRole: TaskRole = isNurse(user) ? 'nurse' : isAlliedHealth(user) ? 'allied-health' : isDoctor(user) ? 'physician' : 'office-manager';

  // A review the engine already raised as store tasks (PROD-3283) shows ONLY as those tasks (whoever holds them), so its
  // checkbox can never bypass the task's owner. Raising is best effort per item: a review is replaced only when EVERY one
  // of its items has a task. A raised, unclaimed task whose step names me also shows in My tasks.
  const store = tab === 'mine' ? mine : team;
  const raised = new Set(
    [...(mine.data?.items ?? []), ...(team.data?.items ?? [])].flatMap((i) =>
      i.task.run ? [`${i.task.run.instanceId}|${i.task.run.activityId}|${i.task.run.label}`] : [],
    ),
  );
  const covered = (r: { checklist: { instanceId: string; activityId: string; items: readonly string[] | null } }) =>
    !!r.checklist.items &&
    r.checklist.items.length > 0 &&
    r.checklist.items.every((l) => raised.has(`${r.checklist.instanceId}|${r.checklist.activityId}|${l}`));
  const reviewsFor = (which: TaskList) =>
    (careModel.data?.reviews ?? []).filter((r) => !covered(r) && (which === 'mine' ? isMyReview(r, me) : !isMyReview(r, me)));
  // A bare review checkbox ticks the engine directly, so it is offered only when BOTH store lists were read in full: a task
  // missing from a partial read must never leave its review writable behind its owner's back.
  const storeReadAll = mine.isSuccess && team.isSuccess && !mine.data.truncated && !team.data.truncated;
  const reviews = storeReadAll ? reviewsFor(tab) : [];
  const namedToMe = new Set(
    (careModel.data?.reviews ?? []).filter((r) => isMyReview(r, me)).map((r) => `${r.checklist.instanceId}|${r.checklist.activityId}`),
  );
  const mineIds = new Set((mine.data?.items ?? []).map((i) => i.task.id));
  const namedTasks = (team.data?.items ?? []).filter(
    (i) => !i.task.ownerUserId && !!i.task.run && namedToMe.has(`${i.task.run.instanceId}|${i.task.run.activityId}`) && !mineIds.has(i.task.id),
  );
  const rows = tab === 'mine' ? [...(mine.data?.items ?? []), ...namedTasks] : (team.data?.items ?? []);
  // The counts add reviews only when the page shows them (both store lists read in full), never rows it hides.
  const mineCount = (mine.data?.total ?? 0) + namedTasks.length + (storeReadAll ? reviewsFor('mine').length : 0);
  const teamCount = (team.data?.total ?? 0) + (storeReadAll ? reviewsFor('team').length : 0);
  // A care-model task keeps the checklist's guards (#774 tickBlock): read the step it came from; no read, no Complete.
  const steps = new Map((careModel.data?.reviews ?? []).map((r) => [`${r.checklist.instanceId}|${r.checklist.activityId}`, r.checklist]));
  const readAll = careModel.isSuccess && !careModel.data.incomplete;
  const stepGone = (t: ClinicalTask) => !!t.run && readAll && !steps.has(`${t.run.instanceId}|${t.run.activityId}`);
  const blockFor = (t: ClinicalTask): string | null => {
    if (!isCareModelTask(t)) return null;
    const step = t.run ? steps.get(`${t.run.instanceId}|${t.run.activityId}`) : undefined;
    if (step) return tickBlock(step, me);
    // A complete read that does not list the step: Complete reads THIS run first (moved on: close; still waiting: refuse).
    return stepGone(t) ? null : STEP_UNREAD;
  };

  // "Empty" is said only after BOTH reads succeeded and the care-model read was complete.
  const empty = storeReadAll && rows.length === 0 && careModel.isSuccess && !careModel.data.incomplete && reviews.length === 0;

  const listName = tab === 'mine' ? 'My tasks' : 'Team queue';
  const shown = rows.length + reviews.length;
  let body: ReactNode;
  if (store.isPending) body = <BrandLoading label="Loading tasks" />;
  else if ((mine.isError && !mine.data) || (team.isError && !team.data))
    // The one failed-read pattern (Patients): never an empty list. A failed poll over a list already read keeps the
    // rows and says so in a Banner above the card.
    body = <EmptyState icon="warning" title={STORE_ERROR} ctaLabel="Retry" onCtaClick={() => void Promise.all([mine.refetch(), team.refetch()])} />;
  else if (empty)
    body = (
      <EmptyState
        icon="clipboard"
        title={tab === 'mine' ? 'Nothing assigned to you' : 'The team queue is empty'}
        description={tab === 'mine' ? 'Claim a task from the Team queue, or create one.' : 'New tasks for any role appear here.'}
      />
    );
  else if (shown === 0 && (mine.isPending || team.isPending)) body = <BrandLoading label="Loading tasks" />;
  else if (shown === 0 && careModel.isPending)
    // Both task lists are read and hold nothing for this tab; only the care-model reviews (one read per care model, seconds) are
    // still coming. Say so instead of holding the whole page on a spinner (Samer, 4 Nov) — and never "empty" until they are read.
    body = <EmptyState icon="clipboard" title={NO_TASKS_YET} description={STILL_READING_REVIEWS} />;
  else if (shown === 0)
    // Nothing to list, but the reads were not all complete: the Banner above says which; never "empty".
    body = <EmptyState icon="warning" title="No tasks could be shown" />;
  else
    body = (
      <Table aria-label={listName}>
        <TableHeader>
          <TableRow>
            {TASK_COLUMNS.map((c, i) => (
              <TableHead key={i} data-col={c === '' ? 'actions' : undefined}>
                {c === '' ? <span className="sr-only">Actions</span> : c}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((item) => (
            <TaskRow key={item.task.id} item={item} userId={user?.userId} careModelBlock={blockFor(item.task)} stepGone={stepGone(item.task)} />
          ))}
          {reviews.map((r) => (
            <CareModelRow key={`${r.enrolmentId}|${r.checklist.activityId}`} review={r} heldBy={reviewHolder(r)} />
          ))}
        </TableBody>
      </Table>
    );

  const warnings = [
    (mine.isError && mine.data) || (team.isError && team.data) ? <Banner key="store-error" variant="error">{STORE_STALE}</Banner> : null,
    careModel.isError ? <Banner key="cm-error" variant="error">{CARE_MODEL_ERROR}</Banner> : null,
    careModel.data?.incomplete ? <Banner key="cm-partial" variant="warning">{CARE_MODEL_PARTIAL}</Banner> : null,
    mine.data?.truncated || team.data?.truncated ? <Banner key="truncated" variant="warning">{TRUNCATED}</Banner> : null,
  ].filter(Boolean);

  return (
    <PageScaffold
      variant="list"
      // The Patients list's frame: content at 48px sides / 32px top / 48px bottom, the page header pulled back out to the
      // frame's edges so its hairline runs full width, one hairline card for the list (`.tasks-list` shares the
      // `.patients-list` card rule in the bridge).
      className="tasks-list p-12 pt-8"
      headerProps={{ className: '-mx-12 -mt-8 mb-4' }}
      bodyProps={{ className: V2_CARD_EDGE }}
      header={
        <PageHeader
          title="Tasks"
          meta="Tasks update every 30 seconds, care-model reviews every minute"
          tabs={
            <HeaderTabs
              aria-label="Task lists"
              value={tab}
              onChange={setTab}
              tabs={[
                { id: 'mine', label: tabLabel('My tasks', mine.data ? mineCount : null) },
                { id: 'team', label: tabLabel('Team queue', team.data ? teamCount : null) },
              ]}
            />
          }
          actions={mayCreateTask(user) ? <Button onClick={() => setCreating(true)}>New task</Button> : null}
        />
      }
      subheader={warnings.length > 0 ? <div className="flex flex-col gap-3">{warnings}</div> : undefined}
    >
      <div data-slot="list-card-title">
        <h2>{listName}</h2>
        <span data-slot="list-card-count">
          {careModel.isPending ? 'Reading care-model reviews…' : store.data ? `Showing ${shown.toLocaleString()}` : null}
        </span>
      </div>
      <div data-slot={`tasks-list-${tab}`}>{body}</div>
      {creating ? <CreateTaskDialog open onClose={() => setCreating(false)} myRole={myRole} onCreated={(line) => toast.success(line)} /> : null}
    </PageScaffold>
  );
}
